import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import logo from "../../assets/app/logo_Gloval.png";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#ffffff",
  },
  header: {
    marginBottom: 20,
    borderBottom: 1,
    paddingBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    width: "30%",
  },
  headerRight: {
    width: "70%",
  },
  logo: {
    width: 100,
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
    color: "#1890ff",
    textAlign: "center",
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 15,
    color: "#1890ff",
    borderBottom: 1,
    paddingBottom: 5,
    fontWeight: "bold",
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
  },
  label: {
    width: "65%",
    fontSize: 12,
    fontWeight: "bold",
    color: "rgb(20, 17, 17)",
    textTransform: "uppercase",
  },
  value: {
    width: "35%",
    fontSize: 11,
    color: "#333333",
    paddingLeft: 10,
  },
  referenceValue: {
    fontSize: 10,
    color: "#666",
    marginLeft: 20,
    fontStyle: "italic",
  },
  waterAnalysis: {
    marginTop: 15,
    marginBottom: 15,
  },
  clotheAnalysis: {
    marginTop: 15,
    marginBottom: 15,
    padding: 10,
    backgroundColor: "#f8f8f8",
    borderRadius: 4,
  },
  clotheTitle: {
    fontSize: 14,
    marginBottom: 10,
    color: "#1890ff",
    fontWeight: "bold",
    borderBottom: "1px solid #e8e8e8",
    paddingBottom: 5,
  },
  observation: {
    marginTop: 10,
    padding: 8,
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
  },
  observationTitle: {
    fontSize: 14,
    color: "#1890ff",
    marginBottom: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  observationText: {
    marginTop: 5,
    fontSize: 11,
    lineHeight: 1.5,
    color: "#333333",
  },
  parameterLabel: {
    width: "40%",
    fontSize: 12,
    fontWeight: "bold",
    color: "#rgb(20, 17, 17)",
    textTransform: "uppercase",
  },
  parameterValue: {
    width: "30%",
    fontSize: 11,
    color: "#333333",
    textAlign: "center",
  },
  parameterHeader: {
    width: "30%",
    fontWeight: "bold",
    fontSize: 12,
    color: "rgb(20, 17, 17)",
    textAlign: "center",
  },
  camaraAnalysis: {
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
    backgroundColor: "#f0f8ff",
    borderRadius: 4,
  },
  camaraTitle: {
    fontSize: 14,
    marginBottom: 10,
    color: "#1890ff",
    fontWeight: "bold",
    borderBottom: "1px solid #e8e8e8",
    paddingBottom: 5,
  },
  observationSection: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
    breakInside: "avoid",
    breakBefore: "page",
  },
  signatureImage: {
    height: 100,
    width: 200,
    marginBottom: 10,
    marginTop: 10,
    objectFit: "contain",
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginRight: 20,
  },
  signatureContainer: {
    width: "50%",
    alignItems: "center",
  },
});

const TemplatePDF = ({ data, fileName }) => (
  <Document
    title={fileName}
    author="Gloval"
    subject="Informe Técnico"
    fileName={fileName}
  >
    {/* Página 1: Información General y Análisis de Agua */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Image style={styles.logo} src={logo} />
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.title}>Informe Técnico</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Información General</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Fecha:</Text>
          <Text style={styles.value}>{moment(data.date).format("LL")}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Lavandería:</Text>
          <Text style={styles.value}>{data.laundry}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Túnel 1:</Text>
          <Text style={styles.value}>{data.tunnel1}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Túnel 2:</Text>
          <Text style={styles.value}>{data.tunnel2}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Nº Programa T1:</Text>
          <Text style={styles.value}>{data.programNumber1}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Nº Programa T2:</Text>
          <Text style={styles.value}>{data.programNumber2}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Carga (Kg) Túnel 1:</Text>
          <Text style={styles.value}>
            {data.charge1 ? data.charge1 + " Kg" : "-"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Carga (Kg) Túnel 2:</Text>
          <Text style={styles.value}>
            {data.charge2 ? data.charge2 + " Kg" : "-"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Tiempo Ciclo Túnel 1:</Text>
          <Text style={styles.value}>
            {data.cicleTime1 ? data.cicleTime1 + " s" : "-"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Tiempo Ciclo Túnel 2:</Text>
          <Text style={styles.value}>
            {data.cicleTime2 ? data.cicleTime2 + " s" : "-"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>% Rechazo:</Text>
          <Text style={styles.value}>
            {data.rejectionRate != null ? data.rejectionRate + " %" : "-"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Descalcificador:</Text>
          <Text style={styles.value}>{data.descalcificador ? "Sí" : "No"}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Vapor:</Text>
          <Text style={styles.value}>{data.vapor ? "Sí" : "No"}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Aquaterm:</Text>
          <Text style={styles.value}>{data.aquaterm ? "Sí" : "No"}</Text>
        </View>
      </View>

      {data.water && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Análisis de Agua</Text>
          <View style={styles.waterAnalysis}>
            <View style={styles.row}>
              <Text style={styles.parameterLabel}>Parámetro</Text>
              <Text style={styles.parameterHeader}>Agua Tratada</Text>
              <Text style={styles.parameterHeader}>Valores Objetivo</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.parameterLabel}>Dureza (dH)</Text>
              <Text style={styles.parameterValue}>
                {data.water?.dureza?.aguaTratada || "-"}
              </Text>
              <Text style={styles.parameterValue}>{"<"} 4 dH</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.parameterLabel}>PH</Text>
              <Text style={styles.parameterValue}>
                {data.water?.ph?.aguaTratada || "-"}
              </Text>
              <Text style={styles.parameterValue}>7-8</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.parameterLabel}>Conductividad (µS)</Text>
              <Text style={styles.parameterValue}>
                {data.water?.conductividad?.aguaTratada || "-"}
              </Text>
              <Text style={styles.parameterValue}>{"<"} 1500 µS</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.parameterLabel}>Hierro (mg/L)</Text>
              <Text style={styles.parameterValue}>
                {data.water?.hierro?.aguaTratada || "-"}
              </Text>
              <Text style={styles.parameterValue}>{"<"} 0.05 mg/L</Text>
            </View>
          </View>
        </View>
      )}
    </Page>

    {/* Página 2: Análisis de Ropa y Secadoras */}
    {(data.clothe?.length > 0 || data.secado?.length > 0) && (
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image style={styles.logo} src={logo} />
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.title}>Informe Técnico - Análisis</Text>
          </View>
        </View>

        {data.clothe && data.clothe.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Análisis de Ropa</Text>
            {data.clothe.map((item, index) => (
              <View key={index} style={styles.clotheAnalysis}>
                <Text style={styles.clotheTitle}>Análisis {index + 1}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>Tipo de ropa:</Text>
                  <Text style={styles.value}>{item.tipo}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.parameterLabel}>Parámetro</Text>
                  <Text style={styles.parameterHeader}>Valor</Text>
                  <Text style={styles.parameterHeader}>Objetivo</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.parameterLabel}>pH</Text>
                  <Text style={styles.parameterValue}>{item.pH ?? "-"}</Text>
                  <Text style={styles.parameterValue}>6.0 - 7.0</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.parameterLabel}>CL2</Text>
                  <Text style={styles.parameterValue}>{item.CL2 ? "Positivo" : "Negativo"}</Text>
                  <Text style={styles.parameterValue}>Negativo</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.parameterLabel}>Hierro</Text>
                  <Text style={styles.parameterValue}>{item.hierro ? "Positivo" : "Negativo"}</Text>
                  <Text style={styles.parameterValue}>Negativo</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.parameterLabel}>Tacto</Text>
                  <Text style={styles.parameterValue}>{item.tacto ? "Suave" : "Malo"}</Text>
                  <Text style={styles.parameterValue}>Suave</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {data.secado && data.secado.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Secado</Text>
            {data.secado.map((item, index) => (
              <View key={index} style={styles.clotheAnalysis}>
                <Text style={styles.clotheTitle}>Secado {index + 1}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>Prenda:</Text>
                  <Text style={styles.value}>{item.prenda}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Temperatura de secado:</Text>
                  <Text style={styles.value}>{item.temperaturaSecado ? item.temperaturaSecado + " ºC" : "-"}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Tiempo de secado:</Text>
                  <Text style={styles.value}>{item.tiempoSecado ?? "-"}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Humedad residual:</Text>
                  <Text style={styles.value}>{item.humedadResidual != null ? item.humedadResidual + " %" : "-"}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Enfriamiento:</Text>
                  <Text style={styles.value}>{item.enfriamiento ? "Bueno" : "Malo"}</Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </Page>
    )}

    {/* Página 3: Cámaras */}
    {data.camaras && data.camaras.length > 0 && (
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image style={styles.logo} src={logo} />
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.title}>Informe Técnico - Cámaras</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cámaras (Lavado)</Text>
          {data.camaras.map((camara, index) => (
            <View key={index} style={styles.camaraAnalysis}>
              <Text style={styles.camaraTitle}>Cámara: {camara.nombre}</Text>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Parámetro</Text>
                <Text style={styles.parameterHeader}>Valor</Text>
                <Text style={styles.parameterHeader}>Objetivo</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Oxígeno Activo (mg/l)</Text>
                <Text style={styles.parameterValue}>{camara.oxigenoActivo ?? "-"}</Text>
                <Text style={styles.parameterValue}>
                  {camara.oxigenoActivoLavado ? "Lav. 150-300" : camara.oxigenoActivoNeutralizado ? "Neutr. <100" : "-"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Cloro Activo (mg/l)</Text>
                <Text style={styles.parameterValue}>{camara.cloroActivo ?? "-"}</Text>
                <Text style={styles.parameterValue}>
                  {camara.cloroActivoLavado ? "Lav. 250-500" : camara.cloroActivoNeutralizado ? "Neutr. 0" : "-"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Alcalinidad Total (mg/l)</Text>
                <Text style={styles.parameterValue}>{camara.alcalinidadTotal ?? "-"}</Text>
                <Text style={styles.parameterValue}>
                  {camara.alcalinidadLavado ? "Lav. 250-800" : "-"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>pH</Text>
                <Text style={styles.parameterValue}>{camara.pH ?? "-"}</Text>
                <Text style={styles.parameterValue}>
                  {camara.phLavado ? "Lav. 9.0-11.5" : camara.phNeutralizado ? "Neutr. 6.0-7.5" : "-"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Consigna Temp.</Text>
                <Text style={styles.parameterValue}>{camara.consignaTemperatura ?? "-"}</Text>
                <Text style={styles.parameterValue}>
                  {camara.temperaturaLavado ? "Lav. > 50" : "-"}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.parameterLabel}>Temperatura Actual</Text>
                <Text style={styles.parameterValue}>{camara.temperaturaActual ?? "-"}</Text>
                <Text style={styles.parameterValue}>-</Text>
              </View>
            </View>
          ))}
        </View>
      </Page>
    )}

    {/* Página 4: Observaciones */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Image style={styles.logo} src={logo} />
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.title}>Informe Técnico - Resumen</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Comentarios</Text>
        <View style={styles.observation}>
          <Text style={styles.observationTitle}>Trabajo Realizado:</Text>
          <Text style={styles.observationText}>{data.workDone}</Text>
        </View>
        <View style={styles.observation}>
          <Text style={styles.observationTitle}>Observaciones:</Text>
          <Text style={styles.observationText}>{data.observation}</Text>
        </View>
      </View>

      {data.signature && (
        <View style={styles.signatureSection}>
          <View style={styles.signatureContainer}>
            <Text style={styles.observationTitle}>Firma cliente:</Text>
            <Image src={data.signature} style={styles.signatureImage} />
          </View>
        </View>
      )}
    </Page>
  </Document>
);

export default TemplatePDF;
